import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Truck, Shield, RotateCcw, Share2, Star, Ruler, Banknote, Clock } from 'lucide-react';
import { formatPrice, calculateDiscount, cn } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { ProductCard } from '../components/products/ProductCard';
import { ReviewCard } from '../components/products/ReviewCard';
import { ReviewForm } from '../components/products/ReviewForm';
import { resolveImageUrl } from '../lib/imageUrl';
import InlineCodOrderForm from '../components/InlineCodOrderForm';
import { toast } from 'sonner';
import { effectiveUnitPrice, hasActiveDiscount } from '../utils/pricing';
import { trackProductView, trackAddToCart } from '../lib/tracker';

const API_URL = '';

const COLOR_MAP = {
  'Noir': '#1A1514',
  'Rose': '#F4A7BB',
  'Champagne': '#F7E7CE',
  'Bordeaux': '#722F37',
  'Rose poudré': '#E8C4C4',
  'Ivoire': '#FFFFF0',
  'Bleu nuit': '#191970',
  'Blanc': '#FFFFFF',
  'Nude': '#E3BC9A',
  'Rouge': '#C0392B',
  'Rose floral': '#FF85A2',
  'Bleu floral': '#6495ED',
  'Crème': '#FFFDD0',
};

export default function ProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [careOpen, setCareOpen] = useState(false);
  const [showCodForm, setShowCodForm] = useState(false);
  
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { user, login } = useAuth();

  const isWishlisted = product ? isInWishlist(product.product_id) : false;

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/products/${productId}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          if (data.colors?.length > 0) {
            const firstColor = typeof data.colors[0] === 'string' ? data.colors[0] : data.colors[0].name;
            setSelectedColor(firstColor);
          }
          // Meta + internal: ViewContent
          trackProductView(
            data.product_id,
            data.name,
            effectiveUnitPrice(data),
            data.category
          );
          
          if (data.category) {
            const relatedRes = await fetch(`${API_URL}/api/products?category=${data.category}&limit=4`);
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              setRelatedProducts(relatedData.products.filter(p => p.product_id !== productId).slice(0, 4));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    window.scrollTo(0, 0);
  }, [productId]);

  const refreshProduct = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      }
    } catch (error) {
      console.error('Error refreshing product:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Veuillez sélectionner une taille');
      return;
    }

    setAddingToCart(true);
    const success = await addToCart(product.product_id, selectedSize, quantity, selectedColor);
    setAddingToCart(false);

    if (success) {
      trackAddToCart(
        product.product_id,
        product.name,
        selectedSize,
        selectedColor,
        effectiveUnitPrice(product)
      );
      toast.success('Ajouté au panier');
    } else {
      toast.error("Erreur lors de l'ajout au panier");
    }
  };

  const handleWishlistClick = async () => {
    if (!user) {
      login();
      return;
    }

    if (isWishlisted) {
      await removeFromWishlist(product.product_id);
      toast.success('Retiré de la wishlist');
    } else {
      await addToWishlist(product.product_id);
      toast.success('Ajouté à la wishlist');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Découvre ${product.name} chez Bloom Lingerie`,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-2xl mb-4">Produit non trouvé</p>
          <Link to="/shop" className="btn-primary px-6 py-3 inline-block">
            Retour à la boutique
          </Link>
        </div>
      </div>
    );
  }

  const hasDiscount = hasActiveDiscount(product, null);
  const discount = hasDiscount ? calculateDiscount(product.price, product.sale_price) : 0;
  const materialText = product.material || 'Tissu de qualité sélectionné';
  const careText = product.care_instructions || 'Lavage à la main recommandé. Séchage à plat.';

  // Live price reflecting current size/color variant — used by both mobile header and desktop column
  const _selectedVariant = (selectedSize && selectedColor)
    ? (product.variants || []).find(v => v.size === selectedSize && v.color === selectedColor)
    : null;
  const displayPrice = effectiveUnitPrice(product, _selectedVariant);
  const showOriginal = displayPrice < product.price;

  const renderHeader = (testIdSuffix = '') => (
    <>
      {/* Badges */}
      <div className="flex gap-2 mb-3">
        {product.is_nouvel_arrivage && (
          <span className="bg-[#D4AF37] text-[#1A1514] text-xs px-3 py-1 uppercase tracking-wider" data-testid={`badge-nouvel-arrivage${testIdSuffix}`}>
            Nouvel Arrivage
          </span>
        )}
        {hasDiscount && (
          <span className="bg-[#1A1514] text-white text-xs px-3 py-1 uppercase tracking-wider" data-testid={`badge-discount${testIdSuffix}`}>
            -{discount}%
          </span>
        )}
      </div>

      {/* Name */}
      <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl mb-3" data-testid={`product-name${testIdSuffix}`}>
        {product.name}
      </h1>

      {/* Price */}
      <div className="flex items-center gap-3">
        {showOriginal ? (
          <>
            <span className="text-2xl font-medium text-[#C87979]" data-testid={`sale-price${testIdSuffix}`}>
              {formatPrice(displayPrice)}
            </span>
            <span className="text-lg text-[#6B6A68] line-through" data-testid={`original-price${testIdSuffix}`}>
              {formatPrice(product.price)}
            </span>
          </>
        ) : (
          <span className="text-2xl font-medium" data-testid={`price${testIdSuffix}`}>
            {formatPrice(displayPrice)}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#1A1514]">Boutique</Link>
          <span>/</span>
          <span className="text-[#1A1514]">{product.name}</span>
        </nav>
      </div>

      {/* Product Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            {/* Mobile-only header (above the photo): badges + name + price */}
            <div className="lg:hidden mb-4" data-testid="mobile-product-header">
              {renderHeader('-mobile')}
            </div>

            <div className="aspect-[3/4] bg-[#F5F4F2] overflow-hidden">
              {product.images?.[currentImageIndex]?.url ? (
                <img
                  src={resolveImageUrl(product.images[currentImageIndex].url)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-serif text-4xl text-[#E0DFDD]">Bloom</span>
                </div>
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={cn(
                      "w-20 h-28 overflow-hidden border-2 transition-colors",
                      currentImageIndex === index ? "border-[#1A1514]" : "border-transparent"
                    )}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={resolveImageUrl(image.url)}
                      alt={`${product.name} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Desktop-only header (mobile shows this above the photo) */}
            <div className="hidden lg:block mb-4">
              {/* Badges */}
              <div className="flex gap-2 mb-4">
                {product.is_nouvel_arrivage && (
                  <span className="bg-[#D4AF37] text-[#1A1514] text-xs px-3 py-1 uppercase tracking-wider">
                    Nouvel Arrivage
                  </span>
                )}
                {hasDiscount && (
                  <span className="bg-[#1A1514] text-white text-xs px-3 py-1 uppercase tracking-wider">
                    -{discount}%
                  </span>
                )}
              </div>

              {/* Name */}
              <h1 className="font-serif text-3xl lg:text-4xl mb-4" data-testid="product-name">
                {product.name}
              </h1>
            </div>

            {/* Rating */}
            {product.review_count > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <div className="star-rating">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < Math.round(product.average_rating) ? 'star-filled fill-current' : 'star-empty'}
                    />
                  ))}
                </div>
                <span className="text-sm text-[#6B6A68]">
                  {product.average_rating.toFixed(1)} ({product.review_count} avis)
                </span>
              </div>
            )}

            {/* Price — desktop only (mobile shows price above the photo) */}
            <div className="hidden lg:flex items-center gap-3 mb-6">
              {showOriginal ? (
                <>
                  <span className="text-2xl font-medium text-[#C87979]" data-testid="sale-price">
                    {formatPrice(displayPrice)}
                  </span>
                  <span className="text-lg text-[#6B6A68] line-through" data-testid="original-price">
                    {formatPrice(product.price)}
                  </span>
                </>
              ) : (
                <span className="text-2xl font-medium" data-testid="price">
                  {formatPrice(displayPrice)}
                </span>
              )}
            </div>

            {/* Color Selector — FIRST on mobile: lets shopper change colors without scrolling away from photo */}
            {(() => {
              // Build color list: available color names filtered by size stock
              const availableColorNames = selectedSize
                ? [...new Set((product.variants || []).filter(v => v.size === selectedSize && v.stock > 0).map(v => v.color).filter(Boolean))]
                : (product.colors || []).map(c => typeof c === 'string' ? c : c.name);
              if (availableColorNames.length === 0) return null;

              // Build color info map from product.colors (supports both string[] and object[])
              const colorInfoMap = {};
              (product.colors || []).forEach(c => {
                if (typeof c === 'string') {
                  colorInfoMap[c] = { name: c, hex_code: COLOR_MAP[c] || '#CCCCCC', image_url: '' };
                } else {
                  colorInfoMap[c.name] = c;
                }
              });

              const handleColorClick = (colorName) => {
                setSelectedColor(colorName);
                // Switch to the photo assigned to this color
                const colorInfo = colorInfoMap[colorName];
                if (colorInfo?.image_url && product.images?.length > 0) {
                  const imgIdx = product.images.findIndex(img => img.url === colorInfo.image_url);
                  if (imgIdx >= 0) setCurrentImageIndex(imgIdx);
                }
              };

              return (
                <div className="mb-6" data-testid="color-selector">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium">Couleur</span>
                    {selectedColor && (
                      <span className="text-sm text-[#6B6A68]">— {selectedColor}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {availableColorNames.map((colorName) => {
                      const info = colorInfoMap[colorName] || { hex_code: COLOR_MAP[colorName] || '#CCCCCC' };
                      const hex = info.hex_code || COLOR_MAP[colorName] || '#CCCCCC';
                      const isLight = ['#FFFFFF', '#FFFFF0', '#FFFDD0', '#F7E7CE'].includes(hex.toUpperCase()) || ['Blanc', 'Ivoire', 'Crème', 'Champagne'].includes(colorName);
                      const variant = selectedSize
                        ? (product.variants || []).find(v => v.size === selectedSize && v.color === colorName)
                        : null;
                      const outOfStock = variant && variant.stock === 0;
                      return (
                        <button
                          key={colorName}
                          onClick={() => !outOfStock && handleColorClick(colorName)}
                          className={cn(
                            "w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center",
                            selectedColor === colorName
                              ? "ring-2 ring-offset-2 ring-[#1A1514] scale-110"
                              : "ring-1 ring-[#E0DFDD] hover:ring-[#1A1514]",
                            outOfStock && "opacity-30 cursor-not-allowed"
                          )}
                          style={{ backgroundColor: hex }}
                          title={colorName + (outOfStock ? ' (Rupture)' : '')}
                          disabled={outOfStock}
                          data-testid={`color-${colorName}`}
                        >
                          {selectedColor === colorName && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M2 7L5.5 10.5L12 3.5" stroke={isLight ? '#1A1514' : '#FFFFFF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Size Selector */}
            <div className="mb-6" data-testid="size-selector">
              <div className="mb-3">
                <span className="text-sm font-medium">Taille</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  // Get unique sizes from variants
                  const sizes = [...new Set((product.variants || []).map(v => v.size))];
                  // Keep SIZES order
                  const orderedSizes = ['S','M','L','XL','XXL','XXXL'].filter(s => sizes.includes(s));
                  // Add any extra sizes not in standard list
                  sizes.forEach(s => { if (!orderedSizes.includes(s)) orderedSizes.push(s); });
                  return orderedSizes.map((size) => {
                    const sizeVariants = (product.variants || []).filter(v => v.size === size);
                    const totalStock = sizeVariants.reduce((sum, v) => sum + (v.stock || 0), 0);
                    return (
                      <button
                        key={size}
                        onClick={() => {
                          if (totalStock > 0) {
                            setSelectedSize(size);
                            // Auto-select first available color for this size
                            const availableColors = sizeVariants.filter(v => v.stock > 0).map(v => v.color);
                            if (availableColors.length > 0 && !availableColors.includes(selectedColor)) {
                              setSelectedColor(availableColors[0]);
                            }
                          }
                        }}
                        className={cn(
                          "size-option",
                          selectedSize === size && "selected",
                          totalStock <= 0 && "out-of-stock"
                        )}
                        disabled={totalStock <= 0}
                        data-testid={`size-${size}`}
                      >
                        {size}
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Small "Add to cart & continue shopping" button — secondary action, styled like the order CTA but smaller */}
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={addingToCart || !selectedSize}
                className="mt-4 bg-[#1A1514] text-white px-4 py-2 text-xs font-medium tracking-wider hover:bg-[#2a2524] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-to-cart-link"
                dir="rtl"
                lang="ar"
              >
                {addingToCart ? '...' : 'أضف للسلة واصل التسوق'}
              </button>
            </div>

            {/* Stock indicator for selected variant */}
            {selectedSize && (() => {
              const variant = selectedColor
                ? (product.variants || []).find(v => v.size === selectedSize && v.color === selectedColor)
                : null;
              const stockCount = variant ? variant.stock : (product.variants || []).filter(v => v.size === selectedSize).reduce((s, v) => s + (v.stock || 0), 0);
              if (stockCount === 0) {
                return (
                  <div className="mb-6 flex items-center gap-2 bg-gray-100 border border-gray-200 px-3 py-2" data-testid="stock-out">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-gray-400"></span>
                    <span className="text-sm font-medium text-gray-500">Rupture de stock</span>
                  </div>
                );
              }
              if (stockCount <= 5) {
                return (
                  <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-2 animate-pulse" data-testid="stock-urgency">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                    </span>
                    <span className="text-sm font-semibold text-red-700">
                      Plus que {stockCount} en stock !
                    </span>
                    <span className="text-xs text-red-500 ml-auto">Commandez vite</span>
                  </div>
                );
              }
              if (stockCount <= 10) {
                return (
                  <div className="mb-6 flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="stock-low">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-400"></span>
                    <span className="text-sm font-medium text-amber-700">
                      Stock limité — {stockCount} restants
                    </span>
                  </div>
                );
              }
              return (
                <p className="text-xs text-green-600 mb-6 flex items-center gap-1.5" data-testid="stock-info">
                  <span className="inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                  En stock
                </p>
              );
            })()}

            {/* Inline COD Order Form (Arabic RTL) */}
            <div className="mb-8" data-testid="inline-cod-wrapper">
              <InlineCodOrderForm
                product={product}
                selectedSize={selectedSize}
                selectedColor={selectedColor}
                quantity={quantity}
                setQuantity={setQuantity}
              />
            </div>

            {/* Description (moved to last position under the order form for cleaner CTA flow) */}
            {product.description && (
              <div className="mb-8" data-testid="product-description-wrapper">
                <h3 className="text-sm font-medium uppercase tracking-wider text-[#1A1514] mb-3 pb-2 border-b border-[#E0DFDD]">
                  Description
                </h3>
                <p className="text-[#6B6A68] leading-relaxed text-sm" data-testid="product-description">
                  {product.description}
                </p>
              </div>
            )}

            {/* Free Shipping Banner */}
            <div className="bg-[#1A1514] text-white px-5 py-3 mb-4 flex items-center justify-center gap-2" data-testid="free-shipping-banner">
              <Truck size={16} className="text-[#D4AF37]" />
              <span className="text-sm font-medium tracking-wide">Livraison GRATUITE à partir de 10 000 DA</span>
            </div>

            {/* COD Reassurance */}
            <div className="bg-[#F4E3E1] border border-[#E8C4C4] px-5 py-3 mb-4 flex items-center justify-center gap-3" data-testid="cod-reassurance-product">
              <Banknote size={18} className="text-[#1A1514] flex-shrink-0" />
              <span className="text-sm font-medium text-[#1A1514]">Paiement à la livraison disponible — <span className="text-[#6B6A68]" dir="rtl">الدفع عند الإستلام</span></span>
            </div>

            {/* Delivery Policy */}
            <div className="bg-[#F9F8F6] border border-[#E0DFDD] p-5 mb-8" data-testid="delivery-policy">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={18} className="text-[#D4AF37]" />
                <span className="text-sm font-semibold">Livraison 24-48H ouvrables</span>
              </div>
              <div className="space-y-2 pl-8">
                <div className="flex items-center gap-2">
                  <Truck size={14} className="text-[#6B6A68]" />
                  <span className="text-xs text-[#6B6A68]">Disponible dans les 58 wilayas</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-[#6B6A68]" />
                  <span className="text-xs text-[#6B6A68]">Paiement à la livraison (COD) disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <RotateCcw size={14} className="text-[#6B6A68]" />
                  <span className="text-xs text-[#6B6A68]">Échange permis pendant 48h</span>
                </div>
              </div>
            </div>

            {/* Product Details Accordion */}
            <div className="border-t border-[#E0DFDD]">
              {/* Material */}
              <button 
                onClick={() => setDetailsOpen(!detailsOpen)}
                className="w-full flex items-center justify-between py-5 text-left"
                data-testid="material-toggle"
              >
                <span className="text-sm font-medium">Matière & Composition</span>
                <span className="text-lg">{detailsOpen ? '−' : '+'}</span>
              </button>
              {detailsOpen && (
                <div className="pb-5 -mt-2" data-testid="material-details">
                  <p className="text-sm text-[#6B6A68] leading-relaxed">
                    <span className="font-medium text-[#1A1514]">Matière :</span> {materialText}
                  </p>
                </div>
              )}

              {/* Care Instructions */}
              <div className="border-t border-[#E0DFDD]">
                <button 
                  onClick={() => setCareOpen(!careOpen)}
                  className="w-full flex items-center justify-between py-5 text-left"
                  data-testid="care-toggle"
                >
                  <span className="text-sm font-medium">Instructions d'entretien</span>
                  <span className="text-lg">{careOpen ? '−' : '+'}</span>
                </button>
                {careOpen && (
                  <div className="pb-5 -mt-2" data-testid="care-details">
                    <p className="text-sm text-[#6B6A68] leading-relaxed">{careText}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-[#E0DFDD]">
        <h2 className="font-serif text-2xl mb-8">Avis Clients ({product.review_count || 0})</h2>
        
        {product.reviews && product.reviews.length > 0 ? (
          <div className="max-w-2xl">
            {product.reviews.map((review) => (
              <ReviewCard key={review.review_id} review={review} />
            ))}
          </div>
        ) : (
          <p className="text-[#6B6A68]">Aucun avis pour le moment. Soyez le premier à donner votre avis!</p>
        )}
        
        <ReviewForm productId={productId} onReviewAdded={refreshProduct} />
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-[#E0DFDD]" data-testid="related-products">
          <h2 className="font-serif text-2xl mb-8">Produits Similaires</h2>
          <div className="product-grid">
            {relatedProducts.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
